import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { INTEGRATIONS_MAPPING_TYPE } from '@/constants/integrations';
import {
  IBynderCollectionsResponse,
  ICalendarByVendorItem,
  ICalendarItem,
  IEmailIntegrationItem,
  IGetHubSpotFieldsMappingResponse,
  IGetHubSpotFieldsResponse,
  IGetSalesforceFieldsMappingResponse,
  IGetSalesforceFieldsResponse,
  IGetSelectedBynderCollectionsResponse,
  IIntegrationItem,
  IIntegrationWebhook,
  INotionDatabaseAndPageItem,
  ISaveMappingsBody,
  ISaveSelectedBynderCollectionsBody,
  ISaveSelectedNotionPagesBody,
  ISalesforceCanvasCredentialsBody,
  ISalesforceCanvasStatus,
  ISalesforceDisconnectResult,
  ISalesforceIntegrationStats,
  ISelectedNotionPagesResponse,
  ISuggestFieldMappingsResponse,
} from '@/types/integrations';
import { apiInstance } from '@/utils';

const receiveCalendlyScript = async (data: { calendlyHtml: string }) => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.receiveCalendlyScript,
  });

  return result.data;
};

export const getGoogleCalendarList = async (): Promise<{
  authId: number;
  calendars: ICalendarItem[];
  message: string;
  profilePictureUrl: string;
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getGoogleCalendarList,
  });

  return result.data;
};

export const selectGoogleCalendar = async (data: { authId: number; calendarId: string }) => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.selectGoogleCalendar,
  });

  return result.data;
};

export type SalesforceEnvironment = 'production' | 'sandbox';

export const addSalesForce = async (
  options: string | { environment: SalesforceEnvironment },
): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    // Preserve callers that still pass the legacy client_id string.
    params: typeof options === 'string' ? { client_id: options } : options,
    url: API_ENDPOINTS.addSalesForce,
  });

  // During a staggered rollout an older API may ignore the sandbox parameter.
  // Require explicit server support before sending a sandbox user to Salesforce.
  if (typeof options !== 'string' && options.environment === 'sandbox' && result.data.environment !== 'sandbox') {
    throw new Error('Sandbox connections are not available yet. Please try again after the update.');
  }

  return result.data;
};

export const addHubSpot = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addHubSpot,
  });

  return result.data;
};

export interface HubSpotAuthStatus {
  authenticated: boolean;
  scopes?: string[];
  missingRequired?: string[];
  missingRecommended?: string[];
  portalId?: string;
  needsReauth?: boolean;
  hasRecommendedMissing?: boolean;
  scopeCount?: number;
  tokenValid?: boolean;
  needsReconnect?: boolean;
  temporaryError?: boolean;
  error?: string;
}

export const getHubSpotAuthStatus = async (): Promise<HubSpotAuthStatus> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getHubSpotAuthStatus,
  });

  return result.data;
};

export const authorizeHubSpotMarketplaceInstall = async (data: {
  workspaceId: number;
  returnUrl: string;
}): Promise<{ success: true; redirectUrl: string; expiresAt: string }> => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.authorizeHubSpotMarketplaceInstall,
  });

  return result.data;
};

export const finalizeHubSpotMarketplaceInstall = async (data: {
  code: string;
  state: string;
  returnUrl: string;
}): Promise<{ success: true; returnUrl: string }> => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.finalizeHubSpotMarketplaceInstall,
  });

  return result.data;
};

export const disconnectHubSpot = async (): Promise<{
  success: boolean;
  disconnected: boolean;
}> => {
  const result = await apiInstance({
    method: 'DELETE',
    url: API_ENDPOINTS.disconnectHubSpot,
  });

  return result.data;
};

export const getSalesforceAuthStatus = async (): Promise<{
  authenticated: boolean;
  instanceUrl?: string;
  environment?: SalesforceEnvironment;
  tokenValid?: boolean;
  needsReconnect?: boolean;
  temporaryError?: boolean;
  error?: string;
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getSalesforceAuthStatus,
  });

  return result.data;
};

export const addApollo = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addApollo,
  });

  return result.data;
};

export interface ISendosoStatus {
  success: boolean;
  /** False when the server holds no Sendoso credential — the card stays hidden. */
  configured: boolean;
  connected: boolean;
  account?: { email: string | null; name: string | null };
  scopes?: string[];
  connectedAt?: string;
}

export const getSendosoStatus = async (): Promise<ISendosoStatus> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getSendosoStatus,
  });

  return result.data;
};

export const addSendoso = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addSendoso,
  });

  return result.data;
};

export const disconnectSendoso = async (): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: 'DELETE',
    url: API_ENDPOINTS.disconnectSendoso,
  });

  return result.data;
};

export const getIntegrationsByChatToken = async (
  chatToken: string,
): Promise<{ integrations: IIntegrationItem[] }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getIntegrationsByChatToken(chatToken),
  });

  return result.data;
};

/**
 * Get all integrations for the current workspace (no chatToken required)
 */
export const listIntegrations = async (): Promise<{ integrations: IIntegrationItem[] }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.listIntegrations,
  });

  return result.data;
};

export const getPublicCalendarData = async (
  slug: string,
): Promise<{
  userData: {
    first_name: string;
    last_name: string;
    profilePictureUrl: string;
    vendorId: number;
    vendorLogo: string;
    vendorUrl: string;
    userId: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getPublicCalendarData(slug),
  });

  return result.data;
};

export const bookMeeting = async (data: {
  date: string;
  time: string;
  email: string;
  name: string;
  topic?: string;
  turnstileToken?: string;
  timeZone?: string;
  timezone?: string;
  authId?: number;
  phone?: string;
  vendorId?: number;
  slug?: string;
  notes?: string;
}) => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.bookMeeting,
  });

  return result.data;
};

export const getEmailIntegrations = async (options?: { writeAccessOnly?: boolean }): Promise<{
  emailIntegrations: IEmailIntegrationItem[];
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getEmailIntegrations,
    params: options?.writeAccessOnly ? { writeAccessOnly: 'true' } : undefined,
  });

  return result.data;
};

export const getSalesforceFields = async (
  type: INTEGRATIONS_MAPPING_TYPE,
): Promise<IGetSalesforceFieldsResponse> => {
  const result = await apiInstance({
    method: 'GET',
    params: {
      type,
    },
    url: API_ENDPOINTS.getSalesforceFields,
  });

  return result.data;
};

export const saveSalesforceMappings = async (data: ISaveMappingsBody) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.saveSalesforceMappings,
  });

  return result.data;
};

export const getSalesforceFieldMappings = async (
  type: INTEGRATIONS_MAPPING_TYPE,
): Promise<IGetSalesforceFieldsMappingResponse> => {
  const result = await apiInstance({
    method: 'GET',
    params: {
      type,
    },
    url: API_ENDPOINTS.getSalesforceFieldMappings,
  });

  return result.data;
};

// PRD #3 — per-vendor Salesforce person_object_mode (Contact | Lead)
export const getSalesforcePersonObjectMode = async (): Promise<{
  success: boolean;
  data: { personObjectMode: 'contact' | 'lead' };
  capabilities?: { preservesOmittedDefaultCollections?: boolean };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.salesforcePersonObjectMode,
  });

  return result.data;
};

export const updateSalesforcePersonObjectMode = async (
  personObjectMode: 'contact' | 'lead',
): Promise<{ success: boolean; data: { personObjectMode: 'contact' | 'lead' } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    data: { personObjectMode },
    url: API_ENDPOINTS.salesforcePersonObjectMode,
  });

  return result.data;
};

// Salesforce integration stats (mb-server#6683). `refresh` bypasses the server's five-minute
// cache, which exists because one read spends about ten of the org's Salesforce API calls.
export const getSalesforceStats = async (
  days: number,
  refresh = false,
): Promise<ISalesforceIntegrationStats> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.salesforceStats,
    params: refresh ? { days, refresh: '1' } : { days },
  });

  return result.data;
};

// Disconnect Salesforce (mb-server#6688). Deletes the workspace's stored credentials and asks
// Salesforce to revoke the grant. Field mappings survive, so reconnecting restores them.
export const disconnectSalesforce = async (): Promise<ISalesforceDisconnectResult> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.salesforceAuth,
  });

  return result.data;
};

// Salesforce Account Panel Canvas credentials (mb-server#5952). The consumer secret is
// write-only: the server stores it encrypted and never returns it.
export const getSalesforceCanvasStatus = async (): Promise<ISalesforceCanvasStatus> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.salesforceCanvas,
  });

  return result.data;
};

export const saveSalesforceCanvasCredentials = async (
  data: ISalesforceCanvasCredentialsBody,
): Promise<ISalesforceCanvasStatus> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    data,
    url: API_ENDPOINTS.salesforceCanvas,
  });

  return result.data;
};

export const clearSalesforceCanvasCredentials = async (): Promise<ISalesforceCanvasStatus> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.salesforceCanvas,
  });

  return result.data;
};

export const getHubspotFields = async (
  type: INTEGRATIONS_MAPPING_TYPE,
): Promise<IGetHubSpotFieldsResponse> => {
  const result = await apiInstance({
    method: 'GET',
    params: {
      type,
    },
    url: API_ENDPOINTS.getHubspotFields,
  });

  return result.data;
};

export const saveHubSpotMappings = async (data: ISaveMappingsBody) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.saveHubSpotMappings,
  });

  return result.data;
};

export const getHubSpotFieldMappings = async (
  type: INTEGRATIONS_MAPPING_TYPE,
): Promise<IGetHubSpotFieldsMappingResponse> => {
  const result = await apiInstance({
    method: 'GET',
    params: {
      type,
    },
    url: API_ENDPOINTS.getHubSpotFieldMappings,
  });

  return result.data;
};

export const suggestFieldMappings = async (data: {
  type: string;
  platform: string;
  crmFields: { name: string; label?: string; type?: string; fieldType?: string; readOnly?: boolean }[];
  currentMappings?: { sourceField: string; sourceLabel?: string; crmField?: string }[];
}): Promise<ISuggestFieldMappingsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.suggestFieldMappings,
  });

  return result.data;
};

export const addLinkedin = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addLinkedin,
  });

  return result.data;
};

export const addNotion = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addNotion,
  });

  return result.data;
};

export const generateWebhookData = async (
  platform: string,
): Promise<{ data: { platform: string; endpoint_url: string } }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.generateWebhook(platform),
  });

  return result.data;
};

export const getWebhook = async (platform: string): Promise<{ data: IIntegrationWebhook }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getWebhook(platform),
  });

  return result.data;
};

export const getWebhookWithReveal = async (platform: string): Promise<{ data: IIntegrationWebhook }> => {
  const result = await apiInstance({
    method: 'GET',
    url: `${API_ENDPOINTS.getWebhook(platform)}?reveal=true`,
  });

  return result.data;
};

export const upsertWebhook = async (
  platform: string,
  data: IIntegrationWebhook,
): Promise<{ data: IIntegrationWebhook }> => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.getWebhook(platform),
  });

  return result.data;
};

export const deleteWebhook = async (platform: string): Promise<{ data: IIntegrationWebhook }> => {
  const result = await apiInstance({
    method: 'DELETE',
    url: API_ENDPOINTS.getWebhook(platform),
  });

  return result.data;
};

export const getCalendarIntegrations = async (): Promise<{
  calendarIntegrations: ICalendarByVendorItem[];
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getCalendarIntegrations,
  });

  return result.data;
};

export const getAllCalendarsByUser = async (): Promise<ICalendarByVendorItem[]> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getAllCalendarsByUser,
  });

  return result.data;
};

/**
 * Fetches email accounts specifically for the current authenticated user.
 * 
 * This service function is used for non-admin users to retrieve only their own
 * connected email accounts, implementing user-specific visibility controls.
 * 
 * @returns {Promise<IEmailIntegrationItem[]>} Array of user's email integration items
 * 
 * @example
 * ```typescript
 * // Usage in a React component via hook
 * const { data: userEmails } = useGetAllEmailAccountsByUser();
 * 
 * // Direct service call (not recommended in components)
 * const userEmails = await getAllEmailAccountsByUser();
 * console.log(userEmails); // [{ id: 185, email: "user@example.com", platform: "google_email", ... }]
 * ```
 * 
 * @api_endpoint `GET /api/private/integration/get-all-email-accounts-by-user`
 * 
 * @response_format
 * ```json
 * [
 *   {
 *     "id": 185,
 *     "first_name": "John",
 *     "last_name": "Doe", 
 *     "email": "john.doe@example.com",
 *     "display_name": "John Doe",
 *     "profile_pic_url": null,
 *     "enabled": true,
 *     "platform": "google_email"
 *   }
 * ]
 * ```
 * 
 * @note Unlike `getEmailIntegrations()`, this endpoint:
 * - Returns a direct array (not wrapped in `{ emailIntegrations: [...] }`)
 * - Only includes current user's email accounts
 * - Missing `status` field compared to workspace-wide endpoint
 * - Used by non-admin users in `useGetFilteredEmailIntegrations()`
 * 
 * @see {@link getEmailIntegrations} for workspace-wide email integrations (admin)
 * @see {@link useGetAllEmailAccountsByUser} for the React hook wrapper
 * @see {@link IEmailIntegrationItem} for response data structure
 */
export const getAllEmailAccountsByUser = async (): Promise<IEmailIntegrationItem[]> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getAllEmailAccountsByUser,
  });

  return result.data;
};

export const getNotionDatabasesAndPages = async (
  query: string,
): Promise<{
  results: INotionDatabaseAndPageItem[];
}> => {
  const result = await apiInstance({
    method: 'POST',
    data: {
      query,
    },
    url: API_ENDPOINTS.getNotionDatabasesAndPages,
  });

  return result.data;
};

export const saveSelectedNotionPages = async (data: ISaveSelectedNotionPagesBody) => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.saveSelectedNotionPages,
  });

  return result.data;
};
export const getSelectedNotionPages = async (): Promise<ISelectedNotionPagesResponse> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getSelectedNotionPages,
  });

  return result.data;
};

export const getSelectedNotionPagesWithStatus = async (): Promise<ISelectedNotionPagesResponse> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getSelectedNotionPagesWithStatus,
  });

  return result.data;
};

export const trainNotionPages = async (data: { pageIds: string[] }) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.trainNotionPages,
  });

  return result.data;
};

export const addBynder = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addBynder,
  });

  return result.data;
};

export const getBynderCollections = async (data: {
  query?: string;
  page?: number;
}): Promise<IBynderCollectionsResponse> => {
  const result = await apiInstance({
    method: 'GET',
    params: { keyword: data?.query, page: data?.page },
    url: API_ENDPOINTS.getBynderCollections,
  });

  return result.data;
};

export const saveSelectedBynderCollections = async (data: ISaveSelectedBynderCollectionsBody) => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.saveSelectedBynderCollections,
  });

  return result.data;
};

export const getSelectedBynderCollections =
  async (): Promise<IGetSelectedBynderCollectionsResponse> => {
    const result = await apiInstance({
      method: 'GET',
      url: API_ENDPOINTS.getSelectedBynderCollections,
    });

    return result.data;
  };

export const getSelectedBynderCollectionsWithStatus =
  async (): Promise<IGetSelectedBynderCollectionsResponse> => {
    const result = await apiInstance({
      method: 'GET',
      url: API_ENDPOINTS.getSelectedBynderCollectionsWithStatus,
    });

    return result.data;
  };

export const trainBynderCollections = async (collectionIds: string[]) => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { collectionIds },
    url: API_ENDPOINTS.trainBynderCollections,
  });

  return result.data;
};

export const getBynderAssets = async (data: { query?: string; page?: number }): Promise<{
  media: Array<{
    id: string;
    name: string;
    description: string;
    extension: string[];
    property_Pillar?: string[];
  }>;
  total: { count: number };
  page?: number;
}> => {
  const result = await apiInstance({
    method: 'GET',
    params: {
      page: data?.page,
      limit: 500,
      type: 'document',
      extension: 'pdf,pptx,doc',
      ...(data?.query ? { query: data.query } : {}),
    },
    url: API_ENDPOINTS.getBynderAssets,
  });
  return result.data;
};

export const saveSelectedBynderAssets = async (data: {
  assets: Array<{
    id: string;
    name: string;
    description?: string;
    extension?: string[];
    property_Pillar?: string[];
  }>;
  chat_token: string;
}) => {
  const result = await apiInstance({
    method: 'POST',
    data,
    url: API_ENDPOINTS.saveSelectedBynderAssets,
  });
  return result.data;
};

export const getSelectedBynderAssets = async (): Promise<{
  assets: Array<{
    id: string;
    name: string;
    description: string;
    extension: string[];
    property_Pillar: string[];
    // Add other fields you need
  }>;
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getSelectedBynderAssets,
  });
  return result.data;
};

export const getSelectedBynderAssetsWithStatus = async () => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getBynderAssetsWithStatus,
  });

  return result.data;
};

export const addHeyGen = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addHeyGen,
  });

  return result.data;
};

export const addGoogleSheets = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addGoogleSheets,
  });

  return result.data;
};

export const addGoogleDrive = async (): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.addGoogleDrive,
  });

  return result.data;
};

// Note: chatToken is deprecated - Vector integration is now vendor-level
export const addVector = async (chatToken?: string): Promise<{ authUrl: string }> => {
  const result = await apiInstance({
    method: 'GET',
    params: chatToken ? { chat_token: chatToken } : undefined,
    url: API_ENDPOINTS.addVector,
  });

  return result.data;
};

export const getVectorSettings = async (): Promise<{ enrich_vector: boolean }> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getVectorSettings,
  });

  return result.data;
};

export const updateVectorSettings = async (data: { enrich_vector: boolean }): Promise<{ success: boolean; enrich_vector: boolean }> => {
  const result = await apiInstance({
    method: 'PATCH',
    url: API_ENDPOINTS.updateVectorSettings,
    data,
  });

  return result.data;
};

export const getGoogleSheetsAuthId = async (): Promise<{
  authId: number;
}> => {
  const result = await apiInstance({
    method: 'GET',
    params: {
      category: 'sheets',
      provider: 'google',
    },
    url: API_ENDPOINTS.getGoogleSheetsAuthId,
  });
  return result.data;
};

export const saveHeyGenApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/heygen/api-token',
  });
  return result.data;
};

export const removeHeyGenApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/heygen/api-token',
  });
  return result.data;
};

export const getHeyGenAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasOAuth: boolean;
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod: string | null;
    expiresAt: string | null;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/heygen/auth-status',
  });
  return result.data;
};

export const getHeyGenAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasOAuth: boolean;
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod: string | null;
    expiresAt: string | null;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/heygen/auth-status?reveal=true',
  });
  return result.data;
};

// 6sense Integration (GH#750)
export const saveSixsenseApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/sixsense/api-token',
  });
  return result.data;
};

export const removeSixsenseApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/sixsense/api-token',
  });
  return result.data;
};

export const getSixsenseAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/sixsense/auth-status',
  });
  return result.data;
};

export const getSixsenseAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/sixsense/auth-status?reveal=true',
  });
  return result.data;
};

// Lusha Integration — email + phone enrichment
export const saveLushaApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/lusha/api-token',
  });
  return result.data;
};

export const removeLushaApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/lusha/api-token',
  });
  return result.data;
};

export const getLushaAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/lusha/auth-status',
  });
  return result.data;
};

export const getApolloAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    expiresAt?: string;
    isExpired?: boolean;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/apollo/auth-status',
  });
  return result.data;
};

export const getLushaAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/lusha/auth-status?reveal=true',
  });
  return result.data;
};

// FullEnrich Integration — email + phone enrichment
export const saveFullEnrichApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/fullenrich/api-token',
  });
  return result.data;
};

export const removeFullEnrichApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/fullenrich/api-token',
  });
  return result.data;
};

export const getFullEnrichAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/fullenrich/auth-status',
  });
  return result.data;
};

export const getFullEnrichAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/fullenrich/auth-status?reveal=true',
  });
  return result.data;
};

// Treg Integration — Apollo/Lusha catalog proxy (BYO)
export const saveTregApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/treg/api-token',
  });
  return result.data;
};

export const removeTregApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/treg/api-token',
  });
  return result.data;
};

export const getTregAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/treg/auth-status',
  });
  return result.data;
};

export const getTregAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/treg/auth-status?reveal=true',
  });
  return result.data;
};

// Hunter Integration — email finding + verification (BYO)
export const saveHunterApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/hunter/api-token',
  });
  return result.data;
};

export const removeHunterApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/hunter/api-token',
  });
  return result.data;
};

export const getHunterAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/hunter/auth-status',
  });
  return result.data;
};

export const getHunterAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/hunter/auth-status?reveal=true',
  });
  return result.data;
};

// LeadIQ Integration — work email + direct phone (BYO)
export const saveLeadIQApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/leadiq/api-token',
  });
  return result.data;
};

export const removeLeadIQApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/leadiq/api-token',
  });
  return result.data;
};

export const getLeadIQAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/leadiq/auth-status',
  });
  return result.data;
};

export const getLeadIQAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/leadiq/auth-status?reveal=true',
  });
  return result.data;
};

// Exa Integration
export const saveExaApiKey = async (data: {
  apiKey: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/exa/api-key',
  });
  return result.data;
};

export const removeExaApiKey = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/exa/api-key',
  });
  return result.data;
};

export const getExaAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiKey: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/exa/auth-status',
  });
  return result.data;
};

export const getExaAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiKey: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiKey?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/exa/auth-status?reveal=true',
  });
  return result.data;
};

export interface HeyReachCampaign {
  id: number;
  name: string;
  status: string;
  total_leads: number;
  // LinkedIn account ids attached to this campaign in HeyReach. Used to
  // filter campaigns by the selected sender in the task drawer (#1816).
  campaignAccountIds: number[];
}

export interface HeyReachAccount {
  id: number;
  name: string;
  emailAddress?: string | null;
  connectionCount: number;
  status: string;
  // Independent HeyReach flags: authIsValid = credentials still valid,
  // isActive = currently assigned to a running campaign (not a usability gate).
  // This preserves auth-expired states without disabling Available senders (#2429).
  authIsValid: boolean;
  isActive: boolean;
  isValidNavigator?: boolean;
  isValidRecruiter?: boolean;
  vendorId?: number | null;
  organizationName?: string | null;
  // Name of the member recorded as owning this seat; `name` already falls back to it
  // when HeyReach has no profile name for the seat.
  ownerName?: string | null;
  // `unfinished` = a LinkedIn login that never completed (no profile was ever learned),
  // usually an abandoned attempt retried as a new seat — not a sender who dropped off.
  connectionState?: HeyReachSeatConnectionState;
  /** Workspace member who owns this seat; null/absent when unknown. */
  ownerUserId?: string | null;
}

export type HeyReachSeatConnectionState = 'ready' | 'reconnect_needed' | 'unfinished';

/**
 * A workspace member who can run outreach but has never connected a LinkedIn seat (#5478).
 *
 * Name only, by design — LinkedIn seats are personal logins and this list renders in a picker
 * every SDR in the workspace can open, so the API never sends an address and a member with no
 * display name is omitted server-side rather than labelled with one.
 */
export interface HeyReachMemberWithoutSeat {
  userId: string;
  name: string;
}

export interface HeyReachAccountsResponse {
  accounts: HeyReachAccount[];
  membersWithoutSeat: HeyReachMemberWithoutSeat[];
  /**
   * The LinkedIn seat belonging to the caller, or null when they have none.
   *
   * A sender picker defaults to this rather than to pool routing. An unset sender hands
   * the send to the workspace's default HeyReach campaign, which holds every connected
   * seat and auto-assigns among them with no concept of who owns the account — so a rep's
   * own sequence goes out as a teammate and the prospect's reply lands in that teammate's
   * inbox (#4880).
   *
   * Optional because a frontend deployed ahead of the backend will not receive it; absent
   * must behave exactly like null, i.e. stay on Automatic.
   */
  ownSeatAccountId?: number | null;
}

export type HeyReachConnectionType = 'managed' | 'byo';
export type HeyReachInboxScrapeMode = 'AllTime' | 'HeyReachOnly';

export interface HeyReachProxyCountry {
  name: string;
  code: string;
}

export interface HeyReachAccountStatus {
  success: boolean;
  accountId: number;
  status: string;
  failureReason?: string | null;
  profile?: {
    fullName?: string;
    email?: string;
    profileUrl?: string;
  } | null;
  salesNavigator?: { authenticated?: boolean } | null;
  recruiter?: { authenticated?: boolean } | null;
}

export interface HeyReachCustomProxy {
  host: string;
  port: number;
  username?: string;
  password?: string;
}

export interface HeyReachAccountProxy {
  isCustomProxy: boolean;
  countryCode?: string | null;
  host?: string | null;
  port?: number | null;
  username?: string | null;
  passwordConfigured?: boolean;
}

export type HeyReachConnectionStatus =
  | 'not_connected'
  | 'connected'
  | 'needs_reconnect'
  | 'temporarily_unavailable';

export type HeyReachConnectionReason =
  | 'missing_key'
  | 'invalid_key'
  | 'decrypt_failed'
  | 'upstream_unavailable'
  | 'validation_pending';

export type HeyReachConnectionScope = 'caller' | 'legacy_shared' | 'managed';

export interface HeyReachStatusResponse {
  success: boolean;
  enabled: boolean;
  platform: string;
  connectionType?: 'byo' | 'managed';
  heyreachAgencyEnabled?: boolean;
  // True when a managed row exists but is still being set up (invitation not yet sent).
  // Used to suppress the managed-onboarding toggle for other users in the workspace.
  managedSetupInProgress?: boolean;
  // Managed connections only: whether the CALLING user has their own LinkedIn seat
  // mapped, independent of the shared workspace key's health. A managed workspace
  // can be fully "connected" while a specific rep has never connected their own
  // sender — this is what distinguishes those two states for the viewer. `null`
  // when not applicable (BYO connections, or no authenticated user).
  viewerHasLinkedInSeat?: boolean | null;
  // Optional while the backend rolls out the structured health contract. Clients
  // fall back to `enabled` when these fields are absent.
  connectionStatus?: HeyReachConnectionStatus;
  reason?: HeyReachConnectionReason | null;
  canReconnect?: boolean;
  connectionScope?: HeyReachConnectionScope | null;
  lastValidatedAt?: string | null;
}

export const getHeyReachCampaigns = async (): Promise<HeyReachCampaign[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachCampaigns,
  });
  return result.data?.campaigns || [];
};

export const getHeyReachAccounts = async (
  connectionType?: HeyReachConnectionType,
  purpose?: 'delivery',
): Promise<HeyReachAccountsResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachAccounts,
    params: connectionType || purpose
      ? { ...(connectionType ? { connectionType } : {}), ...(purpose ? { purpose } : {}) }
      : undefined,
  });
  // Both halves come from one call on purpose: the endpoint hits HeyReach's `li_account/GetAll`
  // on every request and that API is rate limited org-wide, so a second fetch for the roster
  // would double the cost of opening a play.
  return {
    accounts: result.data?.accounts || [],
    membersWithoutSeat: result.data?.membersWithoutSeat || [],
    ownSeatAccountId: result.data?.ownSeatAccountId ?? null,
  };
};

export const getHeyReachProxyCountries = async (
  connectionType: HeyReachConnectionType,
): Promise<HeyReachProxyCountry[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachProxyCountries,
    params: { connectionType },
  });
  return result.data?.countries || [];
};

interface HeyReachConnectBase {
  connectionType: HeyReachConnectionType;
  proxyCountryCode?: string;
  customProxy?: HeyReachCustomProxy;
  inboxScrapeMode: HeyReachInboxScrapeMode;
}

export const connectHeyReachAccount = async (data: HeyReachConnectBase & {
  username: string;
  password: string;
  twoFactorAuthKey?: string;
}): Promise<{ success: boolean; linkedInAccountId: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.connectHeyReachAccount,
    data,
  });
  return result.data;
};

export const connectHeyReachAccountWithCookies = async (data: HeyReachConnectBase & {
  cookies: Array<{
    name: string;
    value: string;
    domain?: string;
    path?: string;
    expires?: number | string | null;
  }>;
  userAgent?: string;
}): Promise<{ success: boolean; linkedInAccountId: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.connectHeyReachAccountWithCookies,
    data,
  });
  return result.data;
};

export const getHeyReachAccountStatus = async (
  accountId: number,
  connectionType: HeyReachConnectionType,
): Promise<HeyReachAccountStatus> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachAccountStatus(accountId),
    params: { connectionType },
  });
  return result.data;
};

const postHeyReachAccountAction = async (
  url: string,
  connectionType: HeyReachConnectionType,
  data: Record<string, unknown> = {},
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url,
    data: { connectionType, ...data },
  });
  return result.data;
};

export const submitHeyReachAccountPin = (
  accountId: number,
  connectionType: HeyReachConnectionType,
  pin: string,
) => postHeyReachAccountAction(
  API_ENDPOINTS.submitHeyReachAccountPin(accountId),
  connectionType,
  { pin },
);

export const reconnectHeyReachAccount = (
  accountId: number,
  connectionType: HeyReachConnectionType,
) => postHeyReachAccountAction(API_ENDPOINTS.reconnectHeyReachAccount(accountId), connectionType);

export const resyncHeyReachAccount = (
  accountId: number,
  connectionType: HeyReachConnectionType,
) => postHeyReachAccountAction(API_ENDPOINTS.resyncHeyReachAccount(accountId), connectionType);

export const reloginHeyReachSalesNavigator = (
  accountId: number,
  connectionType: HeyReachConnectionType,
) => postHeyReachAccountAction(API_ENDPOINTS.reloginHeyReachSalesNavigator(accountId), connectionType);

export const reloginHeyReachRecruiter = (
  accountId: number,
  connectionType: HeyReachConnectionType,
) => postHeyReachAccountAction(API_ENDPOINTS.reloginHeyReachRecruiter(accountId), connectionType);

export const getHeyReachAccountProxy = async (
  accountId: number,
  connectionType: HeyReachConnectionType,
): Promise<HeyReachAccountProxy> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachAccountProxy(accountId),
    params: { connectionType },
  });
  return result.data?.proxy;
};

export const updateHeyReachAccountProxy = async (
  accountId: number,
  connectionType: HeyReachConnectionType,
  proxy: { proxyCountryCode?: string; customProxy?: HeyReachCustomProxy },
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.getHeyReachAccountProxy(accountId),
    data: { connectionType, ...proxy },
  });
  return result.data;
};

export const updateHeyReachAccountInboxConfiguration = async (
  accountId: number,
  connectionType: HeyReachConnectionType,
  inboxScrapeMode: HeyReachInboxScrapeMode,
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.updateHeyReachAccountInbox(accountId),
    data: { connectionType, inboxScrapeMode },
  });
  return result.data;
};

export interface HeyReachOutreachRegionRule {
  linkedInAccountId: number;
  allowedCountryCodes: string[];
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/**
 * Only CONFIGURED seats come back. A seat missing from this list is not region-gated and may
 * contact anywhere — that absence is the product default and must never be rendered as an
 * empty allowlist, which would read like "contact nobody".
 *
 * Workspace-admin only on the backend, so callers gate the request rather than swallowing 403s.
 */
export const getHeyReachOutreachRegions = async (): Promise<HeyReachOutreachRegionRule[]> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachOutreachRegions,
  });
  return result.data?.regionRules || [];
};

/** An empty list clears the rule, returning the seat to contacting anywhere. */
export const updateHeyReachOutreachRegion = async (
  accountId: number,
  allowedCountryCodes: string[],
): Promise<{ success: boolean; regionRule?: HeyReachOutreachRegionRule }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.updateHeyReachOutreachRegion(accountId),
    data: { allowedCountryCodes },
  });
  return result.data;
};

export const removeHeyReachAccount = async (
  accountId: number,
  connectionType: HeyReachConnectionType,
  force = false,
): Promise<{ success: boolean }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.removeHeyReachAccount(accountId),
    params: { connectionType, force },
  });
  return result.data;
};

export const saveHeyReachApiKey = async (data: {
  apiKey: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.saveHeyReachApiKey,
  });
  return result.data;
};

export const removeHeyReachApiKey = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.removeHeyReachApiKey,
  });
  return result.data;
};

export const getHeyReachStatus = async (): Promise<HeyReachStatusResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachStatus,
  });
  return result.data;
};

// =============================================================================
// HeyReach agency onboarding (mb-frontend#1730 / mb-server#1728)
// =============================================================================

// State machine for managed HeyReach onboarding:
// - not_provisioned: no workspace created yet
// - invitation_pending: workspace created, invitation sent but not yet accepted (API key not functional)
// - awaiting_linkedin: invitation accepted (workspace API key works), no LinkedIn account connected yet
// - linkedin_connected: at least one LinkedIn account connected
export type HeyReachAgencyOnboardingState =
  | 'not_provisioned'
  | 'invitation_pending'
  | 'awaiting_linkedin'
  | 'linkedin_connected';

export interface HeyReachAgencyProvisionResponse {
  success: boolean;
  heyreachWorkspaceId?: number;
  invitationUrl?: string | null;
  invitationExpiresAt?: string | null;
  invitationStatus?: string | null;
  linkedinConnectGuideUrl?: string;
  code?: string;
  message?: string;
}

// MarketBetter-managed default delivery campaign readiness (#2004).
export type HeyReachDefaultCampaignStatus =
  | 'missing'
  | 'creating'
  | 'ready'
  | 'failed';

export interface HeyReachDefaultCampaignState {
  status: HeyReachDefaultCampaignStatus;
  campaignId: number | null;
  listId?: number | null;
  managed?: boolean;
}

export interface HeyReachAgencyStatusResponse {
  success: boolean;
  state: HeyReachAgencyOnboardingState;
  invitationUrl?: string | null;
  invitationExpiresAt?: string | null;
  invitationStatus?: string | null;
  // Email of the user who provisioned / was last invited to the managed
  // workspace. Used to decide whether to show the "Join workspace" affordance
  // for additional org members (mb-frontend / mb-server#1926).
  invitedEmail?: string | null;
  linkedInAccountCount?: number;
  linkedInAccountTotalCount?: number;
  // Managed-workspace LinkedIn accounts (resolved via the managed key), present
  // once a managed sender exists, including an auth-invalid sender that needs
  // reconnecting. The connected view renders these so a
  // mixed BYO+managed vendor sees the genuinely-managed accounts, not the BYO
  // senders the shared accounts endpoint would return (mb-server#2800).
  linkedInAccounts?: HeyReachAccount[];
  // Present once state === 'linkedin_connected' (mb-frontend#4719): teammates who
  // could send on LinkedIn but hold no seat yet, so Settings can offer the seat
  // invite beside the connected senders. Names only, never addresses (#5478).
  membersWithoutSeat?: HeyReachMemberWithoutSeat[];
  // Present once state === 'linkedin_connected' (#2004).
  defaultCampaign?: { status: HeyReachDefaultCampaignStatus; campaignId: number | null };
  code?: string;
  message?: string;
  // Set when state='invitation_pending' and a cross-org conflict was detected
  // during self-heal — lets the FE show an actionable error without a separate API call.
  errorCode?: string | null;
  errorMessage?: string | null;
}

export interface HeyReachAgencyInviteMemberResponse {
  success: boolean;
  invitationUrl?: string | null;
  invitationExpiresAt?: string | null;
  invitationStatus?: string | null;
  invitedEmail?: string | null;
  linkedinConnectGuideUrl?: string;
  // Admin-initiated invites only (mb-server#5522). `alreadySeated` means the teammate
  // already had a seat and nothing was created — a success, not an error. `notified`
  // reports whether the invitee's email actually went out; that send is best-effort
  // because the HeyReach invitation already exists by the time it is attempted.
  alreadySeated?: boolean;
  notified?: boolean;
  linkedInAccountId?: number;
  code?: string;
  message?: string;
}

export interface HeyReachAgencyDisconnectResponse {
  success: boolean;
  disconnected?: boolean;
  heyreachWorkspaceId?: number | null;
  // HeyReach public Management API has no DeleteWorkspace endpoint, so the BE
  // performs local cleanup only. These two flags surface the upstream state.
  upstreamWorkspaceRemoved?: boolean;
  manualCleanupRequired?: boolean;
  manualCleanupReason?: string;
  code?: string;
  message?: string;
}

export const postHeyReachAgencyOnboarding = async (
  workspaceName?: string,
): Promise<HeyReachAgencyProvisionResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.postHeyReachAgencyOnboarding,
    data: workspaceName ? { workspaceName } : {},
  });
  return result.data;
};

export const getHeyReachAgencyOnboardingStatus = async (
): Promise<HeyReachAgencyStatusResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getHeyReachAgencyOnboardingStatus,
  });
  return result.data;
};

export const getHeyReachDefaultCampaign = async (
): Promise<HeyReachDefaultCampaignState> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.heyReachDefaultCampaign,
  });
  return result.data?.defaultCampaign;
};

// Idempotent — safe to call repeatedly; never creates a second campaign
// (HeyReach campaigns cannot be deleted). Used by the "retry"/"create" CTA.
export const ensureHeyReachDefaultCampaign = async (
): Promise<HeyReachDefaultCampaignState> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.heyReachDefaultCampaign,
  });
  return result.data?.defaultCampaign;
};

export const deleteHeyReachAgencyOnboarding = async (
): Promise<HeyReachAgencyDisconnectResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteHeyReachAgencyOnboarding,
  });
  return result.data;
};

// `target` invites a teammate instead of the caller (mb-server#5522). Admin-only on the
// server, which also emails the invitee — the admin who finds the missing seat is not the
// person who can accept it. Omit it for the original self-invite (mb-server#1926), which
// stays open to any member.
export const postHeyReachAgencyInviteMember = async (
  target?: { targetUserId?: string; targetEmail?: string },
): Promise<HeyReachAgencyInviteMemberResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.postHeyReachAgencyInviteMember,
    data: target ?? {},
  });
  return result.data;
};

// Email Integration Management Services
export const toggleEmailIntegrationStatus = async (
  authId: number,
  platform: string,
  enabled: boolean,
): Promise<{
  success: boolean;
  message: string;
  integration: {
    id: number;
    enabled: boolean;
    status: string;
    user_email: string;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data: { enabled },
    url: API_ENDPOINTS.toggleEmailIntegration(authId, platform),
  });
  return result.data;
};

export const deleteEmailIntegrationService = async (
  authId: number,
  platform: string,
): Promise<{
  success: boolean;
  message: string;
  integration: {
    id: number;
    user_email: string;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteEmailIntegration(authId, platform),
  });
  return result.data;
};

export const testEmailConnectionService = async (
  authId: number,
  platform: string,
): Promise<{
  success: boolean;
  message: string;
  status: 'healthy' | 'disabled' | 'auth_error' | 'token_expired';
  integration?: {
    email: string;
    enabled: boolean;
    expiresAt: string;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.testEmailConnection(authId, platform),
  });
  return result.data;
};

export const updateMailboxSettingsService = async (
  mailboxId: number,
  platform: 'google_email' | 'microsoft_email',
  settings: {
    dailySendLimit?: number;
  },
): Promise<{
  message: string;
  mailbox: {
    id: number;
    daily_send_limit: number;
    enabled: boolean;
    status: string;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: {
      mailboxId,
      platform,
      settings,
    },
    url: API_ENDPOINTS.updateMailboxSettings,
  });
  return result.data;
};

// Calendar Integration Services
export const toggleCalendarIntegrationStatus = async (
  authId: number,
  platform: 'google' | 'microsoft' | 'calendly',
  enabled: boolean,
): Promise<{
  success: boolean;
  message: string;
  integration: {
    id: number;
    enabled: boolean;
    status: string;
    email: string;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data: { enabled },
    url: API_ENDPOINTS.toggleCalendarIntegration(authId, platform),
  });
  return result.data;
};

export const deleteCalendarIntegrationService = async (
  authId: number,
  platform: 'google' | 'microsoft' | 'calendly',
): Promise<{
  success: boolean;
  message: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteCalendarIntegration(authId, platform),
  });
  return result.data;
};

export const testCalendarConnectionService = async (
  authId: number,
  platform: 'google' | 'microsoft' | 'calendly',
): Promise<{
  success: boolean;
  message: string;
  status: 'healthy' | 'disabled' | 'auth_error' | 'token_expired';
  integration?: {
    email: string;
    enabled: boolean;
    expiresAt: string;
  };
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.testCalendarConnection(authId, platform),
  });
  return result.data;
};

// LinkedIn Share Integration (GH#804)
export const getLinkedInShareAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    isAuthenticated: boolean;
    expiresAt: string | null;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/linkedin/share/auth-status',
  });
  return result.data;
};

export const getLinkedInShareProfile = async (): Promise<{
  success: boolean;
  profile: {
    id: string | null;
    name: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    pictureUrl: string | null;
  } | null;
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/linkedin/share/profile',
  });
  return result.data;
};

export const disconnectLinkedInShare = async (connectionRef: string): Promise<{
  success: boolean;
  message: string;
}> => {
  const result = await apiInstance({
    method: 'DELETE',
    url: '/private/integration/linkedin/share/disconnect',
    params: { connectionRef },
  });
  return result.data;
};

export type LinkedInPostingFrequency = 'weekly' | 'three_weekly';

/** The server's PAUSE_REASONS. */
export type LinkedInPostingPauseReason =
  | 'uncertain_outcome'
  | 'auth_expired'
  | 'permission_denied'
  | 'linkedin_identity_changed'
  | 'invalid_schedule';

export interface LinkedInPostingLastRun {
  state: 'published' | 'failed' | 'skipped' | 'uncertain';
  at: string;
  postUrl: string | null;
  outcomeCode: string | null;
}

export interface LinkedInPostingStatus {
  /** The automatic-posting job is on. While it is off nothing posts, for anyone. */
  available: boolean;
  connection: {
    name: string | null;
    expiresAt: string | null;
    needsReconnect: boolean;
    /** Names the connection this page shows. A save sends it back, so stale pages are refused. */
    ref: string;
  } | null;
  settings: {
    enabled: boolean;
    frequency: LinkedInPostingFrequency;
    /** Null until this person has saved settings. */
    timezone: string | null;
    nextRunAt: string | null;
    pausedReason: LinkedInPostingPauseReason | null;
  };
  lastRun: LinkedInPostingLastRun | null;
}

export const getLinkedInPosting = async (): Promise<LinkedInPostingStatus> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.linkedinPosting,
  });
  return result.data;
};

export const saveLinkedInPosting = async (data: {
  enabled?: boolean;
  connectionRef: string;
  frequency: LinkedInPostingFrequency;
  timezone: string;
}): Promise<LinkedInPostingStatus> => {
  const result = await apiInstance({
    method: 'PUT',
    url: API_ENDPOINTS.linkedinPosting,
    data,
  });
  return result.data;
};

// Gamma Integration — presentation generation API key management
export const saveGammaApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/gamma/api-token',
  });
  return result.data;
};

export const removeGammaApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/gamma/api-token',
  });
  return result.data;
};

export const getGammaAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/gamma/auth-status',
  });
  return result.data;
};

export const getGammaAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/gamma/auth-status?reveal=true',
  });
  return result.data;
};

export const createLinkedInPost = async (data: {
  text: string;
  visibility?: 'PUBLIC' | 'CONNECTIONS';
}): Promise<{
  success: boolean;
  message: string;
  post?: {
    id: string;
    text: string;
    visibility: string;
    createdAt: string;
  };
}> => {
  const result = await apiInstance({
    method: 'POST',
    url: '/private/integration/linkedin/posts',
    data,
  });
  return result.data;
};

// Seamless Integration — email finding + verification (BYO)
export const saveSeamlessApiToken = async (data: {
  apiToken: string;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: '/private/integration/seamless/api-token',
  });
  return result.data;
};

export const removeSeamlessApiToken = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: '/private/integration/seamless/api-token',
  });
  return result.data;
};

export const getSeamlessAuthStatus = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    /** Server rollout gate; Seamless is offered in enrichment menus only when true. */
    enrichmentEnabled?: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/seamless/auth-status',
  });
  return result.data;
};

export const getSeamlessAuthStatusWithReveal = async (): Promise<{
  success: boolean;
  status: {
    hasApiToken: boolean;
    isAuthenticated: boolean;
    authMethod?: string;
    createdAt?: string;
    updatedAt?: string;
    apiToken?: string;
  };
}> => {
  const result = await apiInstance({
    method: 'GET',
    url: '/private/integration/seamless/auth-status?reveal=true',
  });
  return result.data;
};

/** One provider's balance as read from the provider itself (mb-server `enrichment-credits`). */
export interface EnrichmentProviderCredits {
  status: 'ok' | 'invalid_key' | 'rate_limited' | 'unavailable';
  remaining: number | null;
  /** 'credits', or the provider's own unit when it has no credits (Hunter 'searches', ZoomInfo 'records'). */
  unit: string | null;
  /** Whose key was read: the caller's personal key or the workspace key. */
  scope?: 'user' | 'organization';
  checkedAt: string;
}

export interface EnrichmentCreditsResponse {
  success: boolean;
  checkedAt: string;
  /** Connected providers only. */
  providers: Record<string, EnrichmentProviderCredits>;
}

export const getEnrichmentCredits = async (options?: { refresh?: boolean }): Promise<EnrichmentCreditsResponse> => {
  const result = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.enrichmentCredits,
    ...(options?.refresh ? { params: { refresh: true } } : {}),
  });
  return result.data;
};

